import React, { useEffect, useMemo, useState } from 'react'

import { Trans, useTranslation } from 'react-i18next'
import { GetTagContainer, InstructionSection, TagInstallationFooter } from './ImplementTag.styled'
import {
  Button,
  ButtonWithIcon,
  StyledHeader,
  StyledText,
  Tooltip,
} from '@clickcease/cc-client-components-lib'
import { MethodsSelector } from './MethodsSelector'
import { GetTagMethod } from './MethodSelector.models'
import { RenderMethod } from './RenderMethod'
import { useGetDomains } from 'Features/domainsManagement/api/getDomains'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useZendeskChat } from 'Hooks/zendesk/useZendeskChat'
import { StyledLink } from 'Components/Elements/Link/Link.styled'
import { zendeskLinks } from 'Consts/links/models/zendesk'
import { CSS } from '@stitches/react'
import { HelpCenterRedirect } from 'Features/HelpCenterRedirect/HelpCenterRedirect'

type ImplementTagProps = {
  addedDomainAddress: string
  onFinish: () => void
  onBack: () => void
  noFooter?: boolean
  smallIcons?: boolean
  preSelectedMethod?: GetTagMethod
  containerCss?: CSS
  customComponent?: React.ReactNode
}

export const ImplementTag = ({
  addedDomainAddress,
  onFinish,
  onBack,
  noFooter,
  customComponent,
  smallIcons,
  preSelectedMethod,
  containerCss,
}: ImplementTagProps) => {
  const { t } = useTranslation('onboarding')
  const [selectedMethod, setSelectedMethod] = useState<GetTagMethod>(
    preSelectedMethod ?? 'googleTagManager'
  )
  const { openChat } = useZendeskChat()
  const domainsQuery = useGetDomains()

  const showTooltip = selectedMethod === 'googleTagManager'
  const domains = domainsQuery.data?.data.domains
  const disableBack = domains && domains.length > 1

  const domain = useMemo(() => {
    if (domains) return domains.find((domain) => domain.address === addedDomainAddress)
  }, [domains, addedDomainAddress])

  useEffect(() => {
    if (domain?.wordpress) {
      setSelectedMethod('wordpressPlugin')
    }
  }, [domain])

  return (
    <GetTagContainer css={containerCss}>
      <StyledHeader variant={'h1'}>{t('setUpTag.title')}</StyledHeader>
      <StyledText
        variant={'baseLight'}
        css={{ textAlign: 'center', width: '590px', marginTop: '$4' }}
      >
        {t('setUpTag.explainer_line1')}
      </StyledText>
      <StyledText variant={'baseLight'} css={{ textAlign: 'center', width: '590px' }}>
        {t('setUpTag.explainer_line2')}
      </StyledText>
      {customComponent && customComponent}
      <MethodsSelector
        addedDomainAddress={addedDomainAddress}
        selectedMethod={selectedMethod}
        onSelect={setSelectedMethod}
        smallIcons={smallIcons}
      />
      <InstructionSection>
        <StyledText
          variant={'baseBold'}
          css={{ width: '100%', marginTop: '$10', marginBottom: '$6', display: 'flex', gap: '$2' }}
        >
          {t('setUpTag.instructions')}
          {showTooltip && (
            <Tooltip
              trigerStyle={{ style: { color: 'var(--colors-neutral)' } }}
              constentStyle={{ width: '200px' }}
              tooltiptContent={
                <Trans
                  ns="domainManagement"
                  i18nKey="get_tag_modal.gtm_manual_steps.for_more_details"
                >
                  For more detailed instructions or if you need further assistance, please visit our
                  or
                  <HelpCenterRedirect
                    text="help center"
                    article={zendeskLinks.howToAddCheqToWebsiteWithGTM}
                  />
                  or
                  <StyledLink onClick={() => openChat()}>contact support.</StyledLink>
                </Trans>
              }
            />
          )}
        </StyledText>
        <RenderMethod tagMethod={selectedMethod} domain={domain} tagUrl={domain?.tagUrl || ''} />
      </InstructionSection>
      {!noFooter && (
        <TagInstallationFooter>
          <ButtonWithIcon
            disabled={disableBack}
            iconLeft={<FontAwesomeIcon icon={faChevronLeft as IconProp} />}
            onClick={onBack}
            text={t('setUpTag.back')}
            variant="ghost"
          />
          <Button text={t('setUpTag.done')} onClick={onFinish} />
        </TagInstallationFooter>
      )}
    </GetTagContainer>
  )
}
